import { Component, NgZone, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { CelebrationService } from '../../../services/celebration.service';
import { BreakyComponent } from '../breaky/breaky.component';
import { BreakyWardrobeService } from '../../../services/breaky-wardrobe.service';
import { accessoryUrl } from '../../breaky/breaky-accessories';

/** Muestra la celebracion actual: subida de nivel (pantalla completa), karma recibido y puntos ganados. */
@Component({
  selector: 'app-celebration-host',
  standalone: true,
  imports: [CommonModule, BreakyComponent],
  template: `
    <ng-container *ngIf="celebration.current$ | async as ev">
      <!-- Subida de nivel -->
      <div *ngIf="ev.kind === 'levelup'" class="ch-backdrop" role="dialog" aria-modal="true" (click)="celebration.dismiss()">
        <i *ngFor="let p of confetti" class="ch-confetti" [style.left.%]="p.x" [style.animation-delay.s]="p.d" [style.background]="p.c"></i>
        <div class="ch-level" (click)="$event.stopPropagation()">
          <div class="ch-rays" aria-hidden="true"></div>
          <app-breaky pose="levelup" [size]="210" motion="bounce"></app-breaky>
          <p class="ch-kicker">¡Subiste de nivel!</p>
          <div class="ch-badge"><span class="ch-badge-icon">{{ ev.levelIcon }}</span></div>
          <h2 class="ch-title">{{ ev.levelName }}</h2>
          <p class="ch-sub">{{ ev.points | number }} puntos · ¡vas increíble!</p>
          <div class="ch-unlock" *ngIf="ev.unlocked.length">
            <p class="ch-unlock-title">🎁 ¡Breaky tiene {{ ev.unlocked.length === 1 ? 'un accesorio nuevo' : ev.unlocked.length + ' accesorios nuevos' }}!</p>
            <div class="ch-unlock-items">
              <span *ngFor="let u of ev.unlocked" class="ch-unlock-item"><img [src]="url(u.id)" [alt]="u.label" /><em>{{ u.label }}</em></span>
            </div>
            <button type="button" class="ch-btn ch-btn--alt" (click)="wear(ev.unlocked)">Ponérselo a Breaky</button>
          </div>
          <button type="button" class="ch-btn" (click)="celebration.dismiss()">¡Seguir!</button>
        </div>
      </div>

      <!-- Cofre del dia abierto -->
      <div *ngIf="ev.kind === 'chest'" class="ch-backdrop" role="dialog" aria-modal="true" (click)="celebration.dismiss()">
        <i *ngFor="let p of confetti" class="ch-confetti" [style.left.%]="p.x" [style.animation-delay.s]="p.d" [style.background]="p.c"></i>
        <div class="ch-level" (click)="$event.stopPropagation()">
          <div class="ch-rays" aria-hidden="true"></div>
          <app-breaky pose="chest" [size]="220" motion="bounce"></app-breaky>
          <p class="ch-kicker">¡Cofre abierto!</p>
          <h2 class="ch-title">+{{ ev.amount }} 🌿</h2>
          <p class="ch-sub">Completaste todas las misiones de hoy. ¡Vuelve mañana por más!</p>
          <button type="button" class="ch-btn" (click)="celebration.dismiss()">¡Genial!</button>
        </div>
      </div>

      <!-- Recompensa por una accion: pausa completada, karma enviado... -->
      <div *ngIf="ev.kind === 'reward'" class="ch-backdrop ch-backdrop--reward" role="dialog" aria-modal="true" (click)="celebration.dismiss()">
        <i *ngFor="let p of confetti" class="ch-confetti" [style.left.%]="p.x" [style.animation-delay.s]="p.d" [style.background]="p.c"></i>
        <div class="ch-level" (click)="$event.stopPropagation()">
          <div class="ch-rays" aria-hidden="true"></div>
          <div class="ch-hero">
            <i *ngFor="let o of orbs" class="ch-orb" [style.--dx.px]="o.dx" [style.--dy.px]="o.dy" [style.animation-delay.s]="o.d" aria-hidden="true">{{ orbEmoji(ev) }}</i>
            <app-breaky [pose]="ev.pose" [size]="190" motion="bounce"></app-breaky>
          </div>
          <p class="ch-kicker">{{ ev.title }}</p>
          <div class="ch-amount" *ngIf="ev.amount > 0">
            <strong>+{{ shown }}</strong>
            <span>{{ ev.label }}</span>
          </div>
          <p class="ch-sub">{{ ev.subtitle }}</p>
          <button type="button" class="ch-btn" (click)="celebration.dismiss()">¡Genial!</button>
        </div>
      </div>

      <!-- Karma recibido -->
      <div *ngIf="ev.kind === 'karma'" class="ch-karma" role="status" (click)="celebration.dismiss()">
        <div class="ch-karma-breaky"><app-breaky pose="karma_receive" [size]="96" motion="idle"></app-breaky></div>
        <div class="ch-karma-text">
          <strong>+{{ ev.points }} 💜 Karma</strong>
          <span>{{ ev.fromName ? ev.fromName + ' te reconoció' : 'Un compañero te reconoció' }}</span>
          <em *ngIf="ev.message">“{{ ev.message }}”</em>
        </div>
        <i *ngFor="let h of hearts" class="ch-heart" [style.left.%]="h.x" [style.animation-delay.s]="h.d">💜</i>
      </div>

      <!-- Puntos ganados -->
      <div *ngIf="ev.kind === 'points'" class="ch-points" [style.--ch-color]="ev.color" role="status">
        <span class="ch-points-plus">+{{ ev.amount }}</span>
        <span class="ch-points-label">{{ ev.label }}</span>
      </div>
    </ng-container>
  `,
  styles: [
    `
      .ch-backdrop { position: fixed; inset: 0; z-index: 10003; display: flex; align-items: center; justify-content: center; padding: 16px; background: radial-gradient(circle at 50% 35%, rgba(30, 64, 175, 0.97), rgba(2, 6, 23, 0.98)); animation: ch-fade 0.3s ease-out; overflow: hidden; }
      @keyframes ch-fade { from { opacity: 0; } to { opacity: 1; } }
      .ch-level { position: relative; display: flex; flex-direction: column; align-items: center; width: 100%; max-width: 340px; text-align: center; color: #fff; animation: ch-in 0.6s cubic-bezier(0.3, 1.5, 0.5, 1); }
      @keyframes ch-in { 0% { transform: scale(0.5) translateY(40px); opacity: 0; } 100% { transform: scale(1) translateY(0); opacity: 1; } }
      .ch-rays { position: absolute; top: -30px; left: 50%; width: 360px; height: 360px; margin-left: -180px; border-radius: 50%; background: repeating-conic-gradient(rgba(166, 255, 0, 0.22) 0 10deg, transparent 10deg 20deg); -webkit-mask: radial-gradient(circle, #000 18%, transparent 68%); mask: radial-gradient(circle, #000 18%, transparent 68%); animation: ch-spin 14s linear infinite; pointer-events: none; }
      @keyframes ch-spin { to { transform: rotate(360deg); } }
      .ch-kicker { position: relative; margin: 6px 0 8px; font-size: 0.8rem; font-weight: 900; letter-spacing: 0.14em; text-transform: uppercase; color: #a6ff00; }
      .ch-badge { position: relative; display: flex; align-items: center; justify-content: center; width: 96px; height: 96px; border-radius: 50%; background: linear-gradient(135deg, #a6ff00, #10b981); box-shadow: 0 0 40px rgba(166, 255, 0, 0.55); animation: ch-pop 0.7s 0.25s cubic-bezier(0.3, 1.8, 0.5, 1) both; }
      .ch-badge::after { content: ''; position: absolute; inset: -9px; border-radius: 50%; border: 2px solid rgba(166, 255, 0, 0.7); animation: ch-ring 1.8s ease-out infinite; }
      .ch-badge-icon { font-size: 3.1rem; line-height: 1; }
      @keyframes ch-pop { 0% { transform: scale(0) rotate(-40deg); } 100% { transform: scale(1) rotate(0); } }
      @keyframes ch-ring { 0% { transform: scale(0.9); opacity: 1; } 100% { transform: scale(1.5); opacity: 0; } }
      .ch-title { margin: 14px 0 2px; font-size: 2rem; font-weight: 900; text-shadow: 0 0 18px rgba(166, 255, 0, 0.5); }
      .ch-sub { margin: 0 0 18px; font-size: 0.8rem; color: #cbd5e1; }
      .ch-btn { padding: 13px 38px; border: 0; border-radius: 16px; font-size: 0.85rem; font-weight: 900; text-transform: uppercase; letter-spacing: 0.05em; color: #0b131e; background: linear-gradient(135deg, #a6ff00, #10b981); cursor: pointer; box-shadow: 0 6px 0 #0a7a55; transition: transform 0.1s, box-shadow 0.1s; }
      .ch-backdrop--reward { background: radial-gradient(circle at 50% 38%, rgba(6, 95, 70, 0.98), rgba(2, 10, 23, 0.99)); }
      .ch-hero { position: relative; display: flex; align-items: center; justify-content: center; }
      .ch-orb { position: absolute; left: 50%; top: 50%; font-size: 1.3rem; font-style: normal; opacity: 0; pointer-events: none; animation: ch-orb 1.5s cubic-bezier(0.2, 0.8, 0.3, 1) both; }
      @keyframes ch-orb { 0% { transform: translate(-50%, -50%) scale(0.3); opacity: 0; } 15% { opacity: 1; } 100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(1.1) rotate(180deg); opacity: 0; } }
      .ch-amount { display: flex; align-items: baseline; justify-content: center; gap: 8px; margin: 2px 0 4px; animation: ch-amount-pop 0.6s 0.3s cubic-bezier(0.3, 1.8, 0.5, 1) both; }
      .ch-amount strong { font-size: 3.2rem; font-weight: 900; line-height: 1; color: #a6ff00; text-shadow: 0 0 24px rgba(166, 255, 0, 0.7), 0 4px 0 rgba(0, 0, 0, 0.3); font-variant-numeric: tabular-nums; }
      .ch-amount span { font-size: 1.05rem; font-weight: 800; color: #d9f99d; }
      @keyframes ch-amount-pop { 0% { transform: scale(0.2); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
      .ch-unlock { width: 100%; margin: 0 0 14px; padding: 12px; border-radius: 18px; background: rgba(255, 255, 255, 0.1); border: 1px solid rgba(166, 255, 0, 0.4); }
      .ch-unlock-title { margin: 0 0 8px; font-size: 0.8rem; font-weight: 800; color: #fff; }
      .ch-unlock-items { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin-bottom: 10px; }
      .ch-unlock-item { display: flex; flex-direction: column; align-items: center; width: 74px; animation: ch-pop 0.6s 0.5s cubic-bezier(0.3, 1.8, 0.5, 1) both; }
      .ch-unlock-item img { width: 64px; height: 64px; object-fit: contain; filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.4)); }
      .ch-unlock-item em { font-size: 0.62rem; font-style: normal; color: #cbd5e1; }
      .ch-btn--alt { display: block; width: 100%; padding: 10px; font-size: 0.75rem; color: #fff; background: linear-gradient(135deg, #7c3aed, #be185d); box-shadow: 0 5px 0 #5b21b6; }
      .ch-btn--alt:active { box-shadow: 0 1px 0 #5b21b6; }
      .ch-btn:active { transform: translateY(4px); box-shadow: 0 2px 0 #0a7a55; }
      .ch-confetti { position: absolute; top: -14px; width: 9px; height: 14px; border-radius: 2px; opacity: 0; animation: ch-fall 2.8s ease-in infinite; }
      @keyframes ch-fall { 0% { transform: translateY(0) rotate(0); opacity: 1; } 100% { transform: translateY(110vh) rotate(620deg); opacity: 0; } }

      .ch-karma { position: fixed; left: 12px; right: 12px; bottom: calc(16px + env(safe-area-inset-bottom)); z-index: 10003; display: flex; align-items: center; gap: 8px; max-width: 420px; margin: 0 auto; padding: 8px 16px 8px 8px; overflow: hidden; border-radius: 22px; color: #fff; background: linear-gradient(135deg, #7c3aed, #be185d); box-shadow: 0 14px 40px rgba(124, 58, 237, 0.55); animation: ch-up 0.6s cubic-bezier(0.3, 1.5, 0.5, 1); cursor: pointer; }
      @keyframes ch-up { 0% { transform: translateY(120%); } 100% { transform: translateY(0); } }
      .ch-karma-breaky { flex: none; }
      .ch-karma-text { display: flex; flex-direction: column; min-width: 0; }
      .ch-karma-text strong { font-size: 1.05rem; font-weight: 900; }
      .ch-karma-text span { font-size: 0.78rem; opacity: 0.95; }
      .ch-karma-text em { margin-top: 2px; font-size: 0.72rem; opacity: 0.9; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .ch-heart { position: absolute; bottom: -10px; font-size: 1rem; opacity: 0; font-style: normal; animation: ch-float 2.6s ease-in infinite; pointer-events: none; }
      @keyframes ch-float { 0% { transform: translateY(0) scale(0.6); opacity: 0.9; } 100% { transform: translateY(-90px) scale(1.2); opacity: 0; } }

      .ch-points { position: fixed; top: calc(14px + env(safe-area-inset-top)); left: 50%; z-index: 10003; display: flex; align-items: baseline; gap: 6px; padding: 8px 18px; border-radius: 999px; color: #0b131e; background: var(--ch-color, #a6ff00); box-shadow: 0 0 24px var(--ch-color, #a6ff00), 0 8px 20px rgba(0, 0, 0, 0.4); transform: translateX(-50%); animation: ch-drop 2.2s ease-out both; pointer-events: none; }
      .ch-points-plus { font-size: 1.3rem; font-weight: 900; }
      .ch-points-label { font-size: 0.78rem; font-weight: 800; }
      @keyframes ch-drop { 0% { transform: translate(-50%, -80px) scale(0.6); opacity: 0; } 12% { transform: translate(-50%, 0) scale(1.12); opacity: 1; } 22% { transform: translate(-50%, 0) scale(1); } 85% { transform: translate(-50%, 0); opacity: 1; } 100% { transform: translate(-50%, -30px); opacity: 0; } }

      @media (prefers-reduced-motion: reduce) {
        .ch-backdrop, .ch-level, .ch-rays, .ch-badge, .ch-badge::after, .ch-confetti, .ch-karma, .ch-heart, .ch-points { animation: none !important; }
      }
    `
  ]
})
export class CelebrationHostComponent implements OnDestroy {
  readonly celebration = inject(CelebrationService);
  private readonly zone = inject(NgZone);

  /** Numero que sube de 0 al total de la recompensa. */
  shown = 0;
  private countTimer: ReturnType<typeof setInterval> | null = null;
  private readonly countSub = this.celebration.current$.subscribe((ev) => {
    if (this.countTimer) clearInterval(this.countTimer);
    this.countTimer = null;
    if (ev?.kind !== 'reward' || ev.amount <= 0) return;
    const target = ev.amount;
    this.shown = 0;
    const steps = Math.min(24, Math.max(8, target));
    let step = 0;
    // Dentro de la zona de Angular para que cada paso del contador refresque la pantalla.
    this.countTimer = this.zone.run(() => setInterval(() => {
      step++;
      this.shown = Math.min(target, Math.round((target * step) / steps));
      if (this.shown >= target && this.countTimer) {
        clearInterval(this.countTimer);
        this.countTimer = null;
      }
    }, 45));
  });

  orbEmoji(ev: { amount: number; label: string }): string {
    if (ev.amount <= 0) return '✨';
    return ev.label.includes('Karma') ? '💜' : ev.label.includes('Pulse') ? '⚡' : '🌿';
  }

  ngOnDestroy(): void {
    this.countSub.unsubscribe();
    if (this.countTimer) clearInterval(this.countTimer);
  }

  /** Chispas que salen de Breaky en todas direcciones. */
  readonly orbs = Array.from({ length: 14 }, (_, i) => {
    const angle = (i / 14) * Math.PI * 2 + (i % 2) * 0.25;
    const dist = 105 + (i % 3) * 32;
    return { dx: Math.round(Math.cos(angle) * dist), dy: Math.round(Math.sin(angle) * dist), d: 0.15 + (i % 5) * 0.08 };
  });
  private readonly wardrobe = inject(BreakyWardrobeService);
  private readonly router = inject(Router);

  url(id: string): string {
    return accessoryUrl(id);
  }

  /** Se lo pone (el primero desbloqueado) y abre el armario para verlo. */
  wear(unlocked: { id: string }[]): void {
    if (unlocked[0]) this.wardrobe.wear(unlocked[0].id);
    this.celebration.dismiss();
    void this.router.navigate(['/dashboard/mi-breaky']);
  }

  readonly confetti = Array.from({ length: 34 }, (_, i) => ({
    x: (i * 100) / 34 + 1,
    d: (i % 9) * 0.25,
    c: ['#a6ff00', '#00f0ff', '#fbbf24', '#f472b6', '#ffffff'][i % 5]
  }));

  readonly hearts = Array.from({ length: 7 }, (_, i) => ({ x: 8 + i * 13, d: (i % 4) * 0.45 }));
}
